import React, { useState, useEffect } from 'react';
import { apiService } from '../../../../services/api';
import { EnderecoEntrega, CreateEnderecoEntregaData, UpdateEnderecoEntregaData } from '../../../../types/enderecosEntrega';
import { Municipio } from '../../../../types/municipio';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import Select from 'react-select';

interface EnderecoEntregaFormProps {
    opportunityId: number;
    enderecoId?: number;
    endereco?: EnderecoEntrega;
    onSave: () => void;
    onCancel: () => void;
}

const EnderecoEntregaForm: React.FC<EnderecoEntregaFormProps> = ({
    opportunityId,
    enderecoId,
    endereco,
    onSave,
    onCancel
}) => {
    const [municipios, setMunicipios] = useState<Municipio[]>([]);
    const [loading, setLoading] = useState(false);
    const [savingData, setSavingData] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Estados UF
    const estadosBrasileiros = [
        "AC", "AL", "AM", "AP", "BA", "CE", "DF", "ES", "GO", "MA", "MG", "MS", "MT",
        "PA", "PB", "PE", "PI", "PR", "RJ", "RN", "RO", "RR", "RS", "SC", "SE", "SP", "TO"
    ];

    // Tipos de endereço
    const tiposEndereco = [
        { value: "Principal", label: "Principal" },
        { value: "Filial", label: "Filial" },
        { value: "Depósito", label: "Depósito" },
        { value: "Obra", label: "Obra" },
        { value: "Escritório", label: "Escritório" },
        { value: "Outros", label: "Outros" }
    ];

    const validationSchema = Yup.object({
        nome: Yup.string().required('Nome do local é obrigatório'),
        endereco: Yup.string().required('Endereço é obrigatório'),
        numero: Yup.string(),
        complemento: Yup.string(),
        bairro: Yup.string(),
        id_municipio: Yup.number().nullable(),
        estado: Yup.string(),
        cep: Yup.string()
            .test('cep-valido', 'CEP inválido', (value) => {
                if (!value) return true;
                return /^\d{5}-?\d{3}$/.test(value.replace(/\D/g, '').replace(/(\d{5})(\d{3})/, '$1-$2'));
            }),
        contato_responsavel: Yup.string(),
        telefone_contato: Yup.string(),
        tipo_endereco: Yup.string(),
        observacoes: Yup.string(),
        ponto_referencia: Yup.string()
    });

    const formik = useFormik({
        initialValues: {
            nome: endereco?.nome || '',
            endereco: endereco?.endereco || '',
            numero: endereco?.numero || '',
            complemento: endereco?.complemento || '',
            bairro: endereco?.bairro || '',
            id_municipio: endereco?.id_municipio || null,
            estado: endereco?.estado || '',
            cep: endereco?.cep || '',
            contato_responsavel: endereco?.contato_responsavel || '',
            telefone_contato: endereco?.telefone_contato || '',
            tipo_endereco: endereco?.tipo_endereco || '',
            observacoes: endereco?.observacoes || '',
            ponto_referencia: endereco?.ponto_referencia || ''
        },
        validationSchema,
        onSubmit: async (values) => {
            try {
                setSavingData(true);
                setError(null);

                // Formata o CEP antes de enviar (remove qualquer formatação)
                const formattedValues = {
                    ...values,
                    cep: values.cep ? values.cep.replace(/\D/g, '') : '',
                    id_oportunidade: opportunityId,
                    // Converte null para undefined para compatibilidade com os tipos
                    id_municipio: values.id_municipio === null ? undefined : values.id_municipio
                };

                let response;
                if (enderecoId) {
                    // Atualizar endereço existente
                    const updateData: UpdateEnderecoEntregaData = { ...formattedValues };
                    response = await apiService.updateEnderecoEntrega(enderecoId, updateData);
                } else {
                    // Criar novo endereço
                    const createData: CreateEnderecoEntregaData = {
                        ...formattedValues,
                        id_oportunidade: opportunityId
                    };
                    response = await apiService.createEnderecoEntrega(createData);
                }

                if (response.success) {
                    onSave();
                } else {
                    setError(response.message || 'Erro ao salvar endereço de entrega');
                }
            } catch (error) {
                console.error('Erro ao salvar endereço:', error);
                setError('Ocorreu um erro ao salvar o endereço. Por favor, tente novamente.');
            } finally {
                setSavingData(false);
            }
        }
    });

    // Carregar lista de municípios quando o estado mudar
    useEffect(() => {
        const fetchMunicipios = async () => {
            if (!formik.values.estado) {
                setMunicipios([]);
                return;
            }
            
            try {
                setLoading(true);
                // Usa o filtro por UF na API
                const response = await apiService.getMunicipios({ uf: formik.values.estado });

                if (response.success && response.data) {
                    setMunicipios(response.data);
                } else {
                    setMunicipios([]);
                }
            } catch (error) {
                console.error('Erro ao carregar municípios:', error);
                setMunicipios([]);
            } finally {
                setLoading(false);
            }
        };

        fetchMunicipios();
    }, [formik.values.estado]);

    // Formatar CEP ao digitar
    const handleCepChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        let value = e.target.value.replace(/\D/g, '');
        if (value.length > 8) {
            value = value.slice(0, 8);
        }
        if (value.length > 5) {
            value = value.replace(/(\d{5})(\d{1,3})/, '$1-$2');
        }
        formik.setFieldValue('cep', value);
    };

    // Formatar telefone ao digitar
    const handleTelefoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        let value = e.target.value.replace(/\D/g, '');
        if (value.length > 11) {
            value = value.slice(0, 11);
        }

        if (value.length > 10) {
            value = value.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
        } else if (value.length > 6) {
            value = value.replace(/(\d{2})(\d{4})(\d{0,4})/, '($1) $2-$3');
        } else if (value.length > 2) {
            value = value.replace(/(\d{2})(\d{0,5})/, '($1) $2');
        }

        formik.setFieldValue('telefone_contato', value);
    };

    return (
        <form onSubmit={formik.handleSubmit}>
            {error && <div className="alert alert-danger mb-3">{error}</div>}

            <div className="row">
                <div className="col-md-6 mb-3">
                    <label htmlFor="nome" className="form-label">
                        Nome do Local <span className="text-danger">*</span>
                    </label>
                    <input
                        type="text"
                        className={`form-control ${formik.touched.nome && formik.errors.nome ? 'is-invalid' : ''}`}
                        id="nome"
                        name="nome"
                        placeholder="Ex: Depósito Principal"
                        value={formik.values.nome}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                    />
                    {formik.touched.nome && formik.errors.nome && (
                        <div className="invalid-feedback">{formik.errors.nome}</div>
                    )}
                </div>

                <div className="col-md-6 mb-3">
                    <label htmlFor="tipo_endereco" className="form-label">Tipo de Endereço</label>
                    <Select
                        id="tipo_endereco"
                        name="tipo_endereco"
                        placeholder="Selecione o tipo..."
                        options={tiposEndereco}
                        value={tiposEndereco.find(opt => opt.value === formik.values.tipo_endereco) || null}
                        onChange={(option) => formik.setFieldValue('tipo_endereco', option?.value || '')}
                        isClearable
                        className={formik.touched.tipo_endereco && formik.errors.tipo_endereco ? 'is-invalid' : ''}
                    />
                    {formik.touched.tipo_endereco && formik.errors.tipo_endereco && (
                        <div className="invalid-feedback d-block">{formik.errors.tipo_endereco}</div>
                    )}
                </div>
            </div>

            <div className="row">
                <div className="col-md-8 mb-3">
                    <label htmlFor="endereco" className="form-label">
                        Endereço <span className="text-danger">*</span>
                    </label>
                    <input
                        type="text"
                        className={`form-control ${formik.touched.endereco && formik.errors.endereco ? 'is-invalid' : ''}`}
                        id="endereco"
                        name="endereco"
                        placeholder="Rua, Avenida, etc."
                        value={formik.values.endereco}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                    />
                    {formik.touched.endereco && formik.errors.endereco && (
                        <div className="invalid-feedback">{formik.errors.endereco}</div>
                    )}
                </div>

                <div className="col-md-4 mb-3">
                    <label htmlFor="numero" className="form-label">Número</label>
                    <input
                        type="text"
                        className={`form-control ${formik.touched.numero && formik.errors.numero ? 'is-invalid' : ''}`}
                        id="numero"
                        name="numero"
                        value={formik.values.numero}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                    />
                    {formik.touched.numero && formik.errors.numero && (
                        <div className="invalid-feedback">{formik.errors.numero}</div>
                    )}
                </div>
            </div>

            <div className="row">
                <div className="col-md-6 mb-3">
                    <label htmlFor="complemento" className="form-label">Complemento</label>
                    <input
                        type="text"
                        className={`form-control ${formik.touched.complemento && formik.errors.complemento ? 'is-invalid' : ''}`}
                        id="complemento"
                        name="complemento"
                        placeholder="Apto, Sala, Conjunto, etc."
                        value={formik.values.complemento}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                    />
                    {formik.touched.complemento && formik.errors.complemento && (
                        <div className="invalid-feedback">{formik.errors.complemento}</div>
                    )}
                </div>

                <div className="col-md-6 mb-3">
                    <label htmlFor="bairro" className="form-label">Bairro</label>
                    <input
                        type="text"
                        className={`form-control ${formik.touched.bairro && formik.errors.bairro ? 'is-invalid' : ''}`}
                        id="bairro"
                        name="bairro"
                        value={formik.values.bairro}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                    />
                    {formik.touched.bairro && formik.errors.bairro && (
                        <div className="invalid-feedback">{formik.errors.bairro}</div>
                    )}
                </div>
            </div>

            <div className="row">
                <div className="col-md-4 mb-3">
                    <label htmlFor="estado" className="form-label">Estado</label>
                    <select
                        className={`form-select ${formik.touched.estado && formik.errors.estado ? 'is-invalid' : ''}`}
                        id="estado"
                        name="estado"
                        value={formik.values.estado}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                    >
                        <option value="">Selecione o estado</option>
                        {estadosBrasileiros.map(uf => (
                            <option key={uf} value={uf}>{uf}</option>
                        ))}
                    </select>
                    {formik.touched.estado && formik.errors.estado && (
                        <div className="invalid-feedback">{formik.errors.estado}</div>
                    )}
                </div>

                <div className="col-md-4 mb-3">
                    <label htmlFor="id_municipio" className="form-label">Município</label>
                    <Select
                        id="id_municipio"
                        name="id_municipio"
                        placeholder={loading ? "Carregando..." : "Selecione o município"}
                        options={municipios.map(mun => ({
                            value: mun.id,
                            label: mun.nome
                        }))}
                        value={
                            formik.values.id_municipio
                                ? municipios
                                    .filter(m => m.id === formik.values.id_municipio)
                                    .map(m => ({ value: m.id, label: m.nome }))[0]
                                : null
                        }
                        onChange={(option) => formik.setFieldValue('id_municipio', option?.value || null)}
                        isDisabled={loading || !formik.values.estado}
                        isClearable
                        isLoading={loading}
                        className={formik.touched.id_municipio && formik.errors.id_municipio ? 'is-invalid' : ''}
                    />
                    {formik.touched.id_municipio && formik.errors.id_municipio && (
                        <div className="invalid-feedback d-block">{formik.errors.id_municipio}</div>
                    )}
                    {!formik.values.estado && (
                        <small className="text-muted">Selecione um estado primeiro</small>
                    )}
                </div>

                <div className="col-md-4 mb-3">
                    <label htmlFor="cep" className="form-label">CEP</label>
                    <input
                        type="text"
                        className={`form-control ${formik.touched.cep && formik.errors.cep ? 'is-invalid' : ''}`}
                        id="cep"
                        name="cep"
                        placeholder="00000-000"
                        value={formik.values.cep}
                        onChange={handleCepChange}
                        onBlur={formik.handleBlur}
                        maxLength={9}
                    />
                    {formik.touched.cep && formik.errors.cep && (
                        <div className="invalid-feedback">{formik.errors.cep}</div>
                    )}
                </div>
            </div>

            <div className="row">
                <div className="col-md-6 mb-3">
                    <label htmlFor="contato_responsavel" className="form-label">Nome do Responsável</label>
                    <input
                        type="text"
                        className={`form-control ${formik.touched.contato_responsavel && formik.errors.contato_responsavel ? 'is-invalid' : ''}`}
                        id="contato_responsavel"
                        name="contato_responsavel"
                        value={formik.values.contato_responsavel}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                    />
                    {formik.touched.contato_responsavel && formik.errors.contato_responsavel && (
                        <div className="invalid-feedback">{formik.errors.contato_responsavel}</div>
                    )}
                </div>

                <div className="col-md-6 mb-3">
                    <label htmlFor="telefone_contato" className="form-label">Telefone de Contato</label>
                    <input
                        type="text"
                        className={`form-control ${formik.touched.telefone_contato && formik.errors.telefone_contato ? 'is-invalid' : ''}`}
                        id="telefone_contato"
                        name="telefone_contato"
                        placeholder="(00) 00000-0000"
                        value={formik.values.telefone_contato}
                        onChange={handleTelefoneChange}
                        onBlur={formik.handleBlur}
                        maxLength={15}
                    />
                    {formik.touched.telefone_contato && formik.errors.telefone_contato && (
                        <div className="invalid-feedback">{formik.errors.telefone_contato}</div>
                    )}
                </div>
            </div>

            <div className="mb-3">
                <label htmlFor="ponto_referencia" className="form-label">Ponto de Referência</label>
                <input
                    type="text"
                    className={`form-control ${formik.touched.ponto_referencia && formik.errors.ponto_referencia ? 'is-invalid' : ''}`}
                    id="ponto_referencia"
                    name="ponto_referencia"
                    placeholder="Próximo a..."
                    value={formik.values.ponto_referencia || ''}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                />
                {formik.touched.ponto_referencia && formik.errors.ponto_referencia && (
                    <div className="invalid-feedback">{formik.errors.ponto_referencia}</div>
                )}
            </div>

            <div className="mb-3">
                <label htmlFor="observacoes" className="form-label">Observações</label>
                <textarea
                    className={`form-control ${formik.touched.observacoes && formik.errors.observacoes ? 'is-invalid' : ''}`}
                    id="observacoes"
                    name="observacoes"
                    rows={3}
                    placeholder="Informações adicionais sobre o local de entrega"
                    value={formik.values.observacoes}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                />
                {formik.touched.observacoes && formik.errors.observacoes && (
                    <div className="invalid-feedback">{formik.errors.observacoes}</div>
                )}
            </div>

            <div className="d-flex justify-content-end gap-2 mt-4">
                <button
                    type="button"
                    className="btn btn-light"
                    onClick={onCancel}
                    disabled={savingData}
                >
                    Cancelar
                </button>
                <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={savingData}
                >
                    {savingData ? (
                        <>
                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                            Salvando...
                        </>
                    ) : (
                        enderecoId ? 'Atualizar Endereço' : 'Salvar Endereço'
                    )}
                </button>
            </div>
        </form>
    );
};

export default EnderecoEntregaForm;